import React, { useState, useEffect } from "react";
import { Typography, Box, Stack, Paper, IconButton } from "@mui/material";
import TipPopup from "../TipPopup";
import WavePlayerFeed from "../Player/WavePlayerFeed";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseOutlinedIcon from "@mui/icons-material/PauseOutlined";
import { useTranslation } from "react-i18next";
import FavoriteButton from "../../common/FavoriteButton";
import { toast } from 'react-toastify';
import { Images } from "../../assets/common";
import ApiService from "./../../services/api-service";
import { useHistory } from "react-router-dom";
import IosShareIcon from "@mui/icons-material/IosShare";
import notifier from "../../components/Notifier";
import ExplicitBadge from "../ExplicitBadge";
import AIBadge from "../AIBadge";
import { getUserId } from "../../constants/function";
import { SERVER_URL } from "../../constants/api";
import "./FeedCard.scss";

const FeedCard = ({
  item,
  index = 0,
  getName = () => "",
  handleClose = () => {},
  isPlaying = false,
  onPlay = () => {},
  setCardRef = () => {},
  setSelectedAlbum,
  setShowPlaylist,
   userSaves = [],
  onSaveToggle = () => {}, 
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const history = useHistory();
  const streamingActiveRef = React.useRef(null); // tracks musicId of active stream

  // Stable callback ref that reports this card's element to the parent keyed by
  // index, so the parent's ref-setter prop stays referentially stable.
  const handleCardRef = React.useCallback((el) => {
    setCardRef(index, el);
  }, [setCardRef, index]);

  // Bound play handler so onClick/play button pass this card's index.
  const handlePlay = React.useCallback(() => {
    onPlay(index);
  }, [onPlay, index]);

  // Helper function to extract artist ID
  const getArtistId = (artist) => {
    if (!artist) return null;
    
    // If artist is a string, it might be the ID itself
    if (typeof artist === 'string' && artist !== 'undefined' && artist !== 'null') {
      return artist;
    }
    
    // If artist is an object, check common ID fields
    if (typeof artist === 'object') {
      // Check common ID fields in order of preference
      // IMPORTANT: uId is the actual user ID for artists in artistData
      const possibleIds = [
        artist.uId,  // This is the actual user ID in artistData
        artist.uid,  // Alternative casing
        artist.userId,
        artist._id,
        artist.id, 
        artist.artistId,
        artist.artist?._id,
        artist.artist?.id,
        artist.uploader?._id,
        artist.uploader?.id
      ];
      
      // Find first valid ID
      for (const id of possibleIds) {
        if (id && id !== 'undefined' && id !== 'null') {
          return id;
        }
      }
      
      // If artist object has a nested artist property with string ID
      if (typeof artist.artist === 'string' && artist.artist !== 'undefined' && artist.artist !== 'null') {
        return artist.artist;
      }
    }
    
    return null;
  };

  // Only track streaming when this card is the actively-playing card
  useEffect(() => {
    if (isPlaying && item?._id && streamingActiveRef.current !== item._id) {
      // User clicked play on this card — start streaming
      ApiService.STREAM_START({
        musicId: item._id,
        albumId: item.albumId || undefined,
      }).then(() => {
        streamingActiveRef.current = item._id;
        console.log(`🎵 FEED STREAM START: "${item.title}"`);
      }).catch(() => {});
    } else if (!isPlaying && streamingActiveRef.current) {
      // Card stopped playing — stop streaming
      const musicId = streamingActiveRef.current;
      streamingActiveRef.current = null;
      ApiService.STREAM_STOP({ musicId }).catch(() => {});
      console.log(`🎵 FEED STREAM STOP: "${item.title}"`);
    }
    return () => {
      if (streamingActiveRef.current) {
        ApiService.STREAM_STOP({ musicId: streamingActiveRef.current }).catch(() => {});
        streamingActiveRef.current = null;
      }
    };
  }, [isPlaying]);

  if (!item) return null;

  const handleStartStreaming = async () => {
    if (!item?._id || streamingActiveRef.current === item._id) return;
    try {
      await ApiService.STREAM_START({
        musicId: item._id,
        albumId: item.albumId || undefined,
      });
      streamingActiveRef.current = item._id;
      console.log(`🎵 FEED STREAM START: "${item.title}"`);
    } catch (err) {
      console.error('[FeedCard] Failed to start streaming:', err);
    }
  };

  const handleStopStreaming = async () => {
    if (!item?._id || streamingActiveRef.current !== item._id) return;
    streamingActiveRef.current = null;
    try {
      await ApiService.STREAM_STOP({ musicId: item._id });
      console.log(`🎵 FEED STREAM STOP: "${item.title}"`);
    } catch (err) {
      console.error('[FeedCard] Failed to stop streaming:', err);
    }
  };

  return (
    <Box className="feed-card-bg">
      <Paper
        elevation={6}
        className="feed-item"
        ref={handleCardRef}
        data-idx={rest["data-idx"]}
        onClick={handlePlay}
        style={{
          border: isPlaying ? "2px solid var(--primary-color)" : undefined,
          borderRadius: "32px",
          "--bg-img": `url(${item.thumbnail})`,
          background: "rgba(0,0,0,0.70)",
          backgroundBlendMode: "overlay"
        }}
      >
        {/* Title & Artist */}
        <Box className="feed-header">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography className="feed-title" variant="h6">
              {item.title}
            </Typography>
            <AIBadge show={item.isAIGenerated} size="medium" />
            <ExplicitBadge show={item.isExplicit} size="medium" />
          </Stack>
          <Typography className="feed-artist" variant="subtitle2">
            {/* Use artistData if available, otherwise fall back to uploader */}
            {item.artistData && item.artistData[0] ? (
              <>
                <span
                  style={{ cursor: "pointer", textDecoration: "none" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const artistId = getArtistId(item.artistData[0]);
                    console.log("Artist click - extracted ID:", artistId, "from data:", item.artistData[0]);
                    console.log("Using uId:", item.artistData[0].uId, "vs _id:", item.artistData[0]._id);
                    if (artistId) {
                      history.push(`/app/ArtistProfile/${artistId}`);
                    } else {
                      console.error("Artist ID not found for:", item.artistData[0]);
                      notifier.error("Unable to view artist profile");
                    }
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.textDecoration = "underline";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.textDecoration = "none";
                  }}
                >
                  {item.artistData[0].displayArtistName}
                </span>
                {item.artistData[1] && (
                  <>
                    {" "}
                    <span style={{ color: "#bbb" }}>&nbsp;feat.&nbsp;</span>
                    <span
                      style={{ cursor: "pointer", textDecoration: "none" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const artistId = getArtistId(item.artistData[1]);
                        console.log("Featured artist click - extracted ID:", artistId, "from data:", item.artistData[1]);
                        if (artistId) {
                          history.push(`/app/ArtistProfile/${artistId}`);
                        } else {
                          console.error("Featured artist ID not found for:", item.artistData[1]);
                          notifier.error("Unable to view artist profile");
                        }
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.textDecoration = "underline";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.textDecoration = "none";
                      }}
                    >
                      {item.artistData[1].displayArtistName}
                    </span>
                  </>
                )}
              </>
            ) : item.uploader ? (
              // Fallback to uploader if artistData is not available
              <span
                style={{ cursor: "pointer", textDecoration: "none" }}
                onClick={(e) => {
                  e.stopPropagation();
                  const uploaderId = getArtistId(item.uploader);
                  if (uploaderId) {
                    history.push(`/app/ArtistProfile/${uploaderId}`);
                  } else {
                    console.error("Uploader ID not found for:", item.uploader);
                  }
                }}
                onMouseEnter={(e) => {
                  e.target.style.textDecoration = "underline";
                }}
                onMouseLeave={(e) => {
                  e.target.style.textDecoration = "none";
                }}
              >
                {typeof item.uploader === 'object' ? 
                  (item.uploader.displayArtistName || item.uploader.firstName || 'Unknown Artist') : 
                  'Unknown Artist'}
              </span>
            ) : (
              <span>Unknown Artist</span>
            )}
          </Typography>
        </Box>

        {/* Thumbnail */}
        <Box className="feed-thumbnail">
          <img src={item.thumbnail} alt={item.title} loading="lazy" decoding="async" />
        </Box>

        {/* Action Buttons: Tip, Play, Save/Livestream */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          justifyContent="center"
          className="feed-actions"
        >
          {/* Tip Button */}
          <IconButton
            onClick={e => {
              e.stopPropagation();
              setOpen(!open);
              console.log("Tip button clicked for song", item._id);
            }}
            className="tip-btn _cp"
            title="Tip song"
          >
            <img src={Images.tip_player} alt="tip_player" style={{ width: 28, height: 28 }} />
          </IconButton>
          {/* Play Button */}
          <IconButton
            onClick={e => {
              e.stopPropagation();
              handlePlay();
              console.log("Play button clicked for song", item._id, "isPlaying:", isPlaying);
              // Do not call stream here, handled in handleCurrentTime
            }}
            className="play-btn _cp"
            title={isPlaying ? "Pause" : "Play"}
            style={{ transform: "translateY(-10px)" }}
          >
            {!isPlaying ? (
              <PlayArrowRoundedIcon sx={{ fontSize: "30px" }} color="default" className="_cp" />
            ) : (
              <PauseOutlinedIcon sx={{ fontSize: "30px" }} color="default" className="_cp" />
            )}
          </IconButton>

         {/* Favorite Button - Now using the reusable component */}
<Box sx={{ display: 'flex', alignItems: 'center', transform: 'translateY(-10px)' }}>
          <FavoriteButton
            itemId={item._id}
            itemType="music"
            userSaves={userSaves}
            onSaveToggle={onSaveToggle}
            size={24}
                className="favorite-btn"

          />
            </Box>

          {/* Save/Livestream Button */}
          <IconButton
            title={t("dropMenu.m3")}
            onClick={() => {
              setSelectedAlbum(item._id);
              setShowPlaylist(true);
              console.log("Add to playlist/studio clicked for song", item._id);
            }}
          >
            <img
              src={Images.playlist_add}
              alt="add to studio"
              className="w24 h20"
            />
          </IconButton>

          {/* Share Button */}
          <IconButton
            title="Share"
            onClick={async (e) => {
              e.stopPropagation();
              const userId = getUserId();
              const shareUrl = `${SERVER_URL}/share/song/${item._id}${userId ? `?ref=${userId}` : ''}`;
              try {
                await navigator.clipboard.writeText(shareUrl);
                notifier.success('Link copied to clipboard!');
              } catch (err) {
                console.error('Error copying to clipboard:', err);
              }
            }}
          >
            <IosShareIcon sx={{ fontSize: 22, color: '#fff' }} />
          </IconButton>
        </Stack>

        {/* Waveform Player */}
        <WavePlayerFeed
          className="feed-wave"
          url={item.filePath}
          sampleData={item.sampleData}
          volume={50}
          playing={isPlaying}
          handlePlay={() => {}}
          handlePause={() => {}}
          handleCurrentTime={() => {}}
          handleEnd={() => {}}
          handleSeek={() => {}}
        />

        {/* Song Details */}
        <Stack className="feed-details" direction="row" spacing={3}>
          <Box className="detail-box">
            <Typography variant="caption">Genre</Typography>
            <Typography variant="body2">{item.genre || "Unknown"}</Typography>
          </Box>
          <Box className="detail-box">
            <Typography variant="caption">Release Date</Typography>
            <Typography variant="body2">
              {item.releaseDate
                ? new Date(item.releaseDate).toLocaleDateString()
                : "N/A"}
            </Typography>
          </Box>
          <Box className="detail-box">
            <Typography variant="caption">Plays</Typography>
            <Typography variant="body2">{Math.round(item.listens ?? 0)}</Typography>
          </Box>
          {item.isExplicit && (
            <Box className="detail-box explicit">
              <Typography variant="caption">Explicit</Typography>
            </Box>
          )}
        </Stack>

        {/* Tip Popup */}
        {open && (
          <TipPopup
            artist={getName(item)}
            id={item._id}
            open={open}
            isArtist={false}
            handleClose={() => setOpen(false)}
          />
        )}
      </Paper>
    </Box>
  );
};

export default React.memo(FeedCard);
